import { cn, useSidebar } from "@beweegbaas/ui";
import { Link, type LinkComponentProps } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

/** Deterministic per-cell random in [0,1) so the static field needs no stored state. */
function dotHash(x: number, y: number, z: number) {
  let h = Math.imul(x + 1, 0x27d4eb2d) ^ Math.imul(y + 1, 0x165667b1) ^ Math.imul(z, 0x9e3779b9);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Static pixel-dot backdrop for the logo tile: small brand-blue squares on a grid,
 * clustered near the left/right edges so the mark stays clear. */
function LogoDotField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const spacing = 8;
    const size = 2.5;

    const draw = () => {
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      // Dot color comes from the Tailwind text class on the canvas (blue-500/20).
      ctx.fillStyle = getComputedStyle(canvas).color;
      const cols = Math.ceil(w / spacing);
      const rows = Math.ceil(h / spacing);
      for (let i = 0; i <= cols; i++) {
        const edge = Math.abs(i / cols - 0.5) * 2;
        for (let j = 0; j <= rows; j++) {
          // Density falls off toward the horizontal center; a small ambient floor lets sparse
          // dots surround the middle, but it fades out inside the zone the logo occupies.
          const dist = Math.hypot(edge, (Math.abs(j / rows - 0.5) * 2) ** 2);
          const ambient = 0.07 * Math.min(1, Math.max(0, (dist - 0.45) * 4));
          const density = Math.min(1, ambient + edge ** 4.5 * 1.6);
          if (density < 0.01 || dotHash(i, j, 0) >= density) continue;
          // Per-dot variation on top of the /20 base alpha baked into the color.
          ctx.globalAlpha = 0.25 + dotHash(i, j, 7) * 0.75;
          ctx.fillRect(
            i * spacing + (spacing - size) / 2,
            j * spacing + (spacing - size) / 2,
            size,
            size,
          );
        }
      }
    };

    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    draw();

    return () => observer.disconnect();
  }, []);

  return (
    <canvas ref={canvasRef} aria-hidden className="absolute inset-0 size-full text-blue-500/20" />
  );
}

/** Sidebar-header logo tile linking to the section root. Shared by AppSidebar and AdminSidebar. */
export function SidebarLogoLink({ className, ...props }: LinkComponentProps) {
  const { state, isMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;

  return (
    <Link
      {...props}
      className={cn(
        "flex gap-3 items-center justify-center",
        collapsed
          ? "min-h-0 mb-2"
          : `relative overflow-hidden min-h-20 px-5 border bg-white-500/10 supports-squircle:rounded-2xl
            squircle rounded-sm hover:bg-sidebar-accent/30`,
        className,
      )}
    >
      {!collapsed && <LogoDotField />}
      <img src="/mark-blue.svg" alt="Beweegbaas" className="relative h-8" />
    </Link>
  );
}
